/* The video section's player (sections/vp-video).
 *
 * The section prints its poster and blocks; the video waits in a `<template>`, so not one byte of
 * it is asked for until this decides it may play. It plays when all of these hold: the section is
 * at least 15% on screen, the tab is showing, the visitor has not paused it, and either they pressed
 * play or the section's start rule allows it and they have not asked for reduced motion or data
 * saving. Only the video for the current width is put in; the other one is never fetched. The
 * poster stays until the video has drawn a frame, so the picture never blinks to black.
 *
 * One module for every instance on the page; each `<vp-video>` keeps its own state and drops its
 * listeners when the section is removed or redrawn in the editor. */

const PHONE = '(max-width: 749px)';

// `body` is this theme's scroller; observing against it keeps the section's visibility right.
const scroller = () => {
  const b = document.body;
  return getComputedStyle(b).overflowY !== 'visible' && b.scrollHeight > b.clientHeight ? b : null;
};

class VpVideo extends HTMLElement {
  connectedCallback() {
    this.mount();
  }

  disconnectedCallback() {
    this.destroy();
  }

  mount() {
    if (this.life) return;
    this.life = new AbortController();
    this.button = this.querySelector('[data-vp-video-toggle]');
    this.sound = this.querySelector('[data-vp-video-sound]');
    this.status = this.querySelector('[data-vp-video-status]');
    this.holder = this.querySelector('[data-vp-video-player]');
    this.closer = this.querySelector('[data-vp-video-close]');
    this.pp = this.querySelector('[data-vp-video-pp]');
    this.fill = this.querySelector('[data-vp-video-fill]');
    if (!this.button || !this.holder) return;

    this.holder.replaceChildren();
    delete this.dataset.frame;
    this.phone = matchMedia(PHONE);
    this.calm = matchMedia('(prefers-reduced-motion: reduce)');
    this.asked = false; // the visitor pressed play
    this.stopped = false; // the visitor paused
    this.blocked = false; // the browser refused, or the file failed
    this.ended = false;
    this.seen = false;
    this.loud = false; // the visitor turned the sound on
    this.closed = false; // the visitor closed the player
    this.ready = this.dataset.start === 'visible';
    this.variant = this.pick();
    this.dataset.state = 'poster';
    this.button.hidden = !this.template();
    if (this.sound) this.sound.hidden = this.button.hidden;

    this.on(this.button, 'click', () => this.toggle());
    if (this.sound) this.on(this.sound, 'click', () => this.speak());
    if (this.closer) this.on(this.closer, 'click', () => this.close());
    if (this.pp) this.on(this.pp, 'click', () => this.toggle());
    this.on(document, 'visibilitychange', () => this.sync());
    this.on(this.calm, 'change', () => this.sync());
    if (navigator.connection) this.on(navigator.connection, 'change', () => this.sync());

    // Crossing the phone width swaps the video; the one leaving is stopped and emptied first.
    this.on(this.phone, 'change', () => {
      const next = this.pick();
      if (next === this.variant) return;
      this.release();
      this.variant = next;
      this.ended = false;
      this.button.hidden = !this.template();
      if (this.sound) this.sound.hidden = this.button.hidden;
      this.dataset.state = 'poster';
      this.paint();
      this.sync();
    });

    // Pause at zero; start again only past 15%, so the edge of the screen cannot flicker it.
    if ('IntersectionObserver' in window) {
      this.watch = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || entry.intersectionRatio === 0) this.seen = false;
          else if (entry.intersectionRatio >= 0.15) this.seen = true;
          this.sync();
        },
        { root: scroller(), threshold: [0, 0.15] }
      );
      this.watch.observe(this);
    }

    // "Once the page has loaded": after the load event and the poster's first paint, so the video
    // never competes with what the visitor sees first.
    const signal = this.life.signal;
    const poster = this.querySelector('[data-vp-video-poster]');
    const painted = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (signal.aborted) return;
          this.ready = true;
          this.sync();
        })
      );
    const loaded = () => {
      if (poster && !poster.complete) {
        this.on(poster, 'load', painted, { once: true });
        this.on(poster, 'error', painted, { once: true });
      } else painted();
    };
    if (document.readyState === 'complete') loaded();
    else this.on(window, 'load', loaded, { once: true });

    this.paint();
  }

  on(target, type, listener, options = {}) {
    target.addEventListener(type, listener, { ...options, signal: this.life.signal });
  }

  // `data-phone="none"` keeps a desktop video off phones, which then keep the poster.
  pick() {
    if (!this.phone.matches) return 'desktop';
    if (this.querySelector('template[data-vp-video-variant="mobile"]')) return 'mobile';
    return this.dataset.phone === 'none' ? 'none' : 'desktop';
  }

  template() {
    return this.querySelector(`template[data-vp-video-variant="${this.variant}"]`);
  }

  allowed() {
    return (
      this.life &&
      this.isConnected &&
      this.seen &&
      !document.hidden &&
      !this.stopped &&
      !this.blocked &&
      !this.ended &&
      (this.asked ||
        (this.ready && this.dataset.start !== 'press' && !this.calm.matches && !navigator.connection?.saveData))
    );
  }

  toggle() {
    if (this.pending || (this.video && !this.video.paused)) {
      this.stopped = true;
      this.sync();
      return;
    }
    if (this.dataset.state === 'error') this.release();
    const handoff = this.pp && document.activeElement === this.button;
    this.asked = true;
    this.closed = false;
    this.stopped = this.blocked = this.ended = false;
    // Measured here rather than waiting for the observer, so `play()` stays inside the press:
    // Safari only allows a start that the press itself makes.
    const box = this.getBoundingClientRect();
    this.seen = box.bottom > 0 && box.top < innerHeight && box.right > 0 && box.left < innerWidth;
    if (this.video?.ended) this.video.currentTime = 0;
    this.sync();
    // The middle button steps aside as the player opens, so its focus moves to the pause.
    if (handoff && this.dataset.open === 'true') this.pp.focus();
  }

  // Close: stopped, back to the start under the poster, and the middle button again.
  close() {
    const back = this.contains(document.activeElement);
    this.closed = this.stopped = true;
    this.asked = false;
    if (this.video) {
      this.pause();
      this.video.currentTime = 0;
    }
    delete this.dataset.frame;
    this.dataset.state = 'poster';
    this.paint();
    this.track();
    if (back) this.button.focus();
  }

  // The timeline follows the video once a frame while it plays.
  track() {
    if (!this.fill) return;
    cancelAnimationFrame(this.raf);
    const video = this.video;
    const draw = () => {
      const part = video?.duration ? video.currentTime / video.duration : 0;
      this.fill.style.transform = `scaleX(${part})`;
      if (video && this.video === video && !video.paused) this.raf = requestAnimationFrame(draw);
    };
    draw();
  }

  activate() {
    const template = this.template();
    if (!template) return;
    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;

    video.removeAttribute('poster');
    video.querySelectorAll('img').forEach((image) => image.remove());
    video.removeAttribute('autoplay');
    video.muted = !this.loud;
    video.playsInline = true;
    video.tabIndex = video.controls ? 0 : -1;
    if (video.controls) video.setAttribute('aria-label', this.querySelector('[data-vp-video-poster]')?.alt || 'Video');
    else video.setAttribute('aria-hidden', 'true');
    this.video = video;

    this.media = new AbortController();
    const signal = this.media.signal;
    const on = (type, handler) => video.addEventListener(type, handler, { signal });

    on('playing', () => {
      if (!this.allowed()) {
        this.pause();
        return;
      }
      this.dataset.state = 'playing';
      this.status.textContent = '';
      this.paint();
      this.track();
      // Two frames after `playing` a frame is on screen. Not `requestVideoFrameCallback`: an iPhone
      // decodes a video that asked for one before playing, but never draws it.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!signal.aborted && video.readyState >= 2) this.reveal(video);
        })
      );
    });

    // A start from the native controls counts as the visitor asking for it.
    on('play', () => {
      if (!this.starting) {
        this.auto = false;
        this.asked = true;
        this.stopped = this.blocked = this.ended = false;
      }
      if (!this.allowed()) this.pause();
      this.paint();
    });

    on('pause', () => {
      if (!this.auto && !video.ended) this.stopped = true;
      if (!this.blocked && !this.closed) this.dataset.state = 'paused';
      this.paint();
      this.track();
    });

    on('ended', () => {
      this.ended = true;
      this.dataset.state = 'paused';
      this.paint();
      this.track();
    });

    on('timeupdate', () => {
      if (video.currentTime > 0) this.reveal(video);
    });

    // A failing <source> does not bubble, and can leave play() waiting forever.
    const failed = new Set();
    video.addEventListener(
      'error',
      (event) => {
        if (event.target === video) this.fail('error');
        else {
          failed.add(event.target);
          if (failed.size === video.querySelectorAll('source').length) this.fail('error');
        }
      },
      { capture: true, signal }
    );

    this.holder.replaceChildren(fragment);
    // iOS Safari leaves a `preload="none"` video brought in from a template idle on `play()`,
    // its promise pending forever, unless it is loaded first.
    video.load();
  }

  sync() {
    if (!this.allowed()) {
      this.pause();
      return;
    }
    if (!this.video) this.activate();
    const video = this.video;
    if (!video || this.pending || !video.paused) return;

    this.pending = true;
    this.auto = false;
    this.starting = true;
    this.dataset.state = 'loading';
    this.paint();

    try {
      Promise.resolve(video.play())
        .then(() => {
          if (this.video !== video) return;
          this.pending = this.starting = false;
          if (!this.allowed()) this.pause();
          this.paint();
        })
        .catch((error) => {
          if (this.video !== video) return;
          this.pending = this.starting = false;
          // A pause or a swap interrupted the start; try again if it is still wanted.
          if (error.name === 'AbortError') {
            this.paint();
            if (this.allowed()) this.sync();
            return;
          }
          // A start with sound the browser won't allow without a press goes again without it.
          if (error.name === 'NotAllowedError' && !video.muted) {
            this.loud = false;
            video.muted = true;
            this.paint();
            if (this.allowed()) this.sync();
            return;
          }
          this.fail(error.name === 'NotAllowedError' ? 'blocked' : 'error');
        });
    } catch {
      this.fail('error');
    }
  }

  // The sound button: on starts the video too, inside the press, so the browser allows the sound.
  speak() {
    this.loud = !this.loud;
    if (this.video) this.video.muted = !this.loud;
    if (this.loud && (!this.video || this.video.paused)) this.toggle();
    this.paint();
  }

  reveal(video) {
    if (this.video !== video || !this.allowed() || video.paused || this.dataset.frame === 'true') return;
    this.dataset.frame = 'true';
  }

  pause() {
    if (!this.video) return;
    this.auto = true;
    if (!this.video.paused) this.video.pause();
    this.paint();
  }

  fail(state) {
    this.blocked = true;
    this.pending = this.starting = false;
    this.pause();
    this.dataset.state = state;
    delete this.dataset.frame;
    this.status.textContent = this.dataset[state] || '';
    this.paint();
  }

  // The button says what pressing it will do.
  paint() {
    const going = !this.stopped && (this.pending || (this.video && !this.video.paused));
    this.button.setAttribute('aria-label', going ? this.dataset.pause : this.dataset.play);
    this.button.dataset.playing = String(Boolean(going));
    this.sound?.setAttribute('aria-pressed', String(this.loud));
    if (!this.pp) return;

    // The player is open from the first press or start until it is closed or fails.
    const state = this.dataset.state;
    const open = Boolean(this.video) && !this.closed && Boolean(going || state === 'playing' || state === 'paused');
    this.pp.setAttribute('aria-label', going ? this.dataset.pause : this.dataset.play);
    this.pp.dataset.playing = String(Boolean(going));
    if (!open && this.dataset.open === 'true' && this.closer.parentElement.contains(document.activeElement)) {
      this.button.focus();
    }
    this.dataset.open = String(open);
  }

  release() {
    const video = this.video;
    this.media?.abort();
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.load();
      video.remove();
    }
    this.video = null;
    this.loud = false;
    this.pending = this.starting = false;
    delete this.dataset.frame;
    cancelAnimationFrame(this.raf);
    if (this.fill) this.fill.style.transform = '';
  }

  destroy() {
    this.life?.abort();
    this.watch?.disconnect();
    this.release();
    this.life = null;
  }
}

if (!customElements.get('vp-video')) customElements.define('vp-video', VpVideo);
